import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import AetherisSplashAuth from './AetherisSplashAuth';
import EnterGate from './EnterGate';
import SignupForm from './SignupForm';
import LoginForm from './LoginForm';
import AdminLoginForm from './AdminLoginForm';
import PowerOutageBanner from './PowerOutageBanner';
import MessagePopupNotice from './MessagePopupNotice';
// Code-split below: these are one-screen-at-a-time destinations behind the auth gate, not
// needed on first paint. Splitting them (2026-08-11) took the single 1.4MB main bundle down
// to a small shell + per-page chunks fetched on first navigation to that screen -- the
// pre-split bundle was flagged by Vite's own build warning. The always-needed auth chain
// above (splash/gate/login/signup/admin-login) stays a static import on purpose: every
// visitor hits one of those on first load, so lazy-loading them would only add a network
// round-trip with no benefit.
const Dashboard = lazy(() => import('./Dashboard'));
const AdminDownloadsPage = lazy(() => import('./AdminDownloadsPage'));
const AdminBadMoviesPage = lazy(() => import('./AdminBadMoviesPage'));
const AdminMagnetQueuePage = lazy(() => import('./AdminMagnetQueuePage'));
const AdminPanel = lazy(() => import('./AdminPanel'));
const AdminStatsPage = lazy(() => import('./AdminStatsPage'));

function DownloadsWidget({ session }) {
  const [downloads, setDownloads] = React.useState([]);
  const [collapsed, setCollapsed] = React.useState(false);

  React.useEffect(() => {
    if (!session) return;
    const poll = () => {
      fetch('/api/downloads/active', {
        headers: { Authorization: 'Bearer ' + session.session_token }
      })
        .then(r => r.ok ? r.json() : [])
        .then(data => setDownloads(Array.isArray(data) ? data : []))
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [session]);

  if (!downloads.length) return null;

  return (
    <div style={{
      position: 'fixed', bottom: '100px', right: '24px', zIndex: 2000,
      background: 'rgba(7,5,15,0.97)', border: '1px solid rgba(176,38,255,0.4)',
      borderRadius: '12px', minWidth: '280px', maxWidth: '340px',
      backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 20px rgba(176,38,255,0.1)',
    }}>
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{
          padding: '10px 14px', cursor: 'pointer', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          borderBottom: collapsed ? 'none' : '1px solid rgba(176,38,255,0.2)',
        }}>
        <span style={{ fontSize: '11px', fontFamily: 'Orbitron, monospace', color: '#b026ff', letterSpacing: '0.1em' }}>
          MATERIALIZING ({downloads.length})
        </span>
        <span style={{ color: '#5a5a7a', fontSize: '12px' }}>{collapsed ? '▲' : '▼'}</span>
      </div>
      {!collapsed && (
        <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {downloads.map((d, i) => {
            const pct = d.progress ?? 0;
            const written = d.bytes_written ?? 0;
            const total = d.bytes_total ?? 0;
            const toMB = b => (b / 1048576).toFixed(1);
            return (
              <div key={i}>
                <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.title || d.name || 'Unknown'}
                </div>
                <div style={{ height: '4px', background: '#1a1a2e', borderRadius: '2px', marginBottom: '3px' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg, #b026ff, #cc88ff)', borderRadius: '2px', transition: 'width 0.5s ease' }} />
                </div>
                <div style={{ fontSize: '10px', color: '#5a5a7a', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{pct.toFixed(1)}%</span>
                  {total > 0 && <span>{toMB(written)} / {toMB(total)} MB</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const ActiveUsersPage = lazy(() => import('./ActiveUsersPage'));
const MoviesPage = lazy(() => import('./MoviesPage'));
const LibraryPage = lazy(() => import('./LibraryPage'));
const MovieDetailPage = lazy(() => import('./MovieDetailPage'));
const ActorDetailPage = lazy(() => import('./ActorDetailPage'));
import SideNav from './SideNav'; // persistent chrome, rendered alongside every screen -- stays eager
import ChunkErrorBoundary from './ChunkErrorBoundary';
const AdultPage = lazy(() => import('./AdultPage'));
const SecurityPage = lazy(() => import('./SecurityPage'));
const ForumPage = lazy(() => import('./ForumPage'));
const BBSPage = lazy(() => import('./BBSPage'));
const TVVaultPage = lazy(() => import('./TVVaultPage'));
const TVUserVaultPage = lazy(() => import('./TVUserVaultPage'));
const TVPage = lazy(() => import('./TVPage'));
const AnimePage = lazy(() => import('./AnimePage')); // 2026-08-16: dedicated Anime page
const ComicsPage = lazy(() => import('./ComicsPage')); // 2026-08-26: dedicated Comic Books page
const TVDetailPage = lazy(() => import('./TVDetailPage'));
import { clearSessionToken, setSessionToken, logout as apiLogout, sendHeartbeat, wakeServer, waitForServerAwake, checkServerAwake, resolveAndRipTrack, searchMusicLastfm } from './api';
import ServerWakingScreen from './ServerWakingScreen';
const UserVaultPage = lazy(() => import('./UserVaultPage'));
import ForgotPasswordFlow from './ForgotPasswordFlow';
import TotpLoginStep from './TotpLoginStep';
const TotpSetupPage = lazy(() => import('./TotpSetupPage'));
const VerifyEmailPage = lazy(() => import('./VerifyEmailPage'));
const ResetPasswordPage = lazy(() => import('./ResetPasswordPage'));
import { transitionSoundscape } from './audioEngine';
const SoftwareSearchPage = lazy(() => import('./SoftwareSearchPage'));
const SoftwarePage = lazy(() => import('./SoftwarePage'));
const SearchPage = lazy(() => import('./SearchPage'));
const AccountPage = lazy(() => import('./AccountPage'));
const TransfersPage = lazy(() => import('./TransfersPage'));
const MessagesPage = lazy(() => import('./MessagesPage'));
const NetworkPage = lazy(() => import('./NetworkPage'));
const ContactPage = lazy(() => import('./ContactPage'));
const MusicPage = lazy(() => import('./MusicPage'));
const WiiGamesPage = lazy(() => import('./WiiGamesPage'));
const ReadyToWatchModal = lazy(() => import('./ReadyToWatchModal'));
const PlayerOverlay = lazy(() => import('./PlayerOverlay'));
import { getAdultCacheStatus, buildPlayableUrl, markAdultWatched, verifyParentalPassword } from './api';
import globalBg from './assets/Aetherisbackground1.webp';

// Shared Suspense fallback for lazy-loaded screens/overlays -- deliberately minimal (this
// only shows for the brief gap while a chunk downloads, usually invisible on a warm cache)
// but still on-brand rather than a blank flash.
const ScreenLoadingFallback = () => (
  <div style={{
    minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#7a4faa', fontFamily: 'Orbitron, monospace', fontSize: '11px', letterSpacing: '0.2em',
  }}>
    LOADING…
  </div>
);

// Maps SideNav's nav keys to the canonical parental-lockable section keys (see
// go-api's parentalLockableSections in handlers/parental.go). Keys with no entry here
// (search, dashboard, user-vault, contact, messages) aren't lockable.
const PARENTAL_SECTION_MAP = {
  'movies-browse': 'movies',
  'library-browse': 'movies',
  'tv': 'tv',
  'music': 'music',
  'games': 'games',
  'software': 'software',
  'bbs': 'bbs',
  'forum': 'forum',
  'adult': 'adult',
};

// 2026-08-28 owner request ("use the uploaded pic as a background on every page"):
// this is Aetherisbackground1.webp -- already an existing asset (imported once in
// MovieDetailPage.jsx but never actually wired into any element there, a dead
// import). pageStyle is applied to literally every page's root wrapper (grep
// confirms ~40 call sites, one per route), so this single change covers the whole
// site. Fixed attachment (not scroll) so it stays put as page content scrolls; a
// dark overlay keeps existing page content/panels legible over it, same purpose
// the earlier hero-banner gradient overlay served.
const pageStyle = {
  background: `linear-gradient(rgba(5,4,12,0.86), rgba(5,4,12,0.86)), url(${globalBg}) center center / cover fixed no-repeat`,
  backgroundColor: '#050508',
  minHeight: '100vh',
  margin: 0,
};

const JesterPopup = ({ onDone }) => {
  const [frame, setFrame] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => setFrame(f => f + 1), 120);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    if (done) { setTimeout(onDone, 800); }
  }, [done]);
  window._jesterDone = () => setDone(true);
  const frames = ['(o)(o)', '(o)\(o)', '(o)-(o)', '(o)/(o)', '(o)|(o)'];
  const ballFrames = [
    ['  *  ','  *  ','  *  '],
    [' *   ','  *  ','   * '],
    ['*    ','  *  ','    *'],
    [' *   ','  *  ','   * '],
  ];
  const f = frame % frames.length;
  const bf = frame % ballFrames.length;
  const balls = ballFrames[bf];
  const jesterX = ((frame * 3) % 80);
  useEffect(() => {
    window._aetherisOpusSupported = opusSupported;
    window._aetherisShowJester = (show) => setShowJester(show);
  }, [opusSupported]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,7,11,0.92)', zIndex: 99999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'Orbitron, monospace' }}>
      {!done ? (
        <>
          <div style={{ fontSize: '11px', color: '#ffe600', letterSpacing: '0.2em', marginBottom: '32px', textAlign: 'center', lineHeight: 2 }}>
            <div>⚠ ANTIQUATED BROWSER DETECTED ⚠</div>
            <div style={{ fontSize: '9px', color: 'rgba(255,230,0,0.6)', marginTop: '8px' }}>YOUR MACHINE IS SO FAR BEHIND THE CURVE</div>
            <div style={{ fontSize: '9px', color: 'rgba(255,230,0,0.6)' }}>WE MUST CONVERT THE SIGNAL TO A FORMAT</div>
            <div style={{ fontSize: '9px', color: 'rgba(255,230,0,0.6)' }}>YOUR ANTIQUE CIRCUITS CAN COMPREHEND</div>
            <div style={{ fontSize: '9px', color: '#ff2a55', marginTop: '8px' }}>TURN YOUR HEARING AID UP — IT'S COMING</div>
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: '13px', color: '#ffe600', background: '#0a0a0f', border: '1px solid rgba(255,230,0,0.3)', borderRadius: '8px', padding: '24px 32px', minWidth: '320px', textAlign: 'center', lineHeight: 2.2 }}>
            <div style={{ position: 'relative', height: '28px', overflow: 'hidden', marginBottom: '8px' }}>
              <div style={{ position: 'absolute', left: `${jesterX}%`, transform: 'translateX(-50%)', transition: 'left 0.1s linear', color: '#ffe600', fontSize: '16px' }}>
                {frames[f]}
              </div>
            </div>
            <div style={{ color: 'rgba(255,230,0,0.5)', fontSize: '11px' }}>{balls[0]}</div>
            <div style={{ color: 'rgba(255,230,0,0.7)', fontSize: '11px' }}>{balls[1]}</div>
            <div style={{ color: 'rgba(255,230,0,0.5)', fontSize: '11px' }}>{balls[2]}</div>
            <div style={{ marginTop: '12px', fontSize: '9px', color: 'rgba(255,230,0,0.4)', letterSpacing: '0.15em' }}>[ TRANSCODING SIGNAL... ]</div>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'monospace', fontSize: '18px', color: '#ffe600', marginBottom: '12px' }}>
            {'(x)(x)'}<br/>
            {'  * * *  '}<br/>
            {' *  *  * '}<br/>
            {'* * * * *'}
          </div>
          <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '10px', color: '#00ff88', letterSpacing: '0.2em' }}>SIGNAL CONVERTED — INITIALIZING PLAYBACK</div>
        </div>
      )}
    </div>
  );
};

// 2026-08-30 owner request: premixed EQ presets for the music player, 10-band peaking
// filters at standard octave-ish frequencies. Gains in dB. "Flat" is silent/no-op.
const EQ_BANDS = [32, 64, 125, 250, 500, 1000, 2000, 4000, 8000, 16000];
const EQ_PRESETS = {
  Flat:          [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  Rock:          [5, 4, 3, 0, -2, -1, 2, 4, 5, 5],
  Pop:           [-1, 2, 4, 4, 2, -1, -2, -2, -1, -1],
  Classical:     [4, 3, 2, 2, 0, -1, -1, 0, 2, 3],
  Jazz:          [3, 2, 1, 2, -1, -1, 0, 1, 2, 3],
  'Bass Boost':  [7, 6, 4, 2, 0, 0, 0, 0, 0, 0],
  'Vocal Boost': [-2, -1, 0, 2, 4, 4, 3, 1, 0, -1],
  Electronic:    [5, 4, 1, 0, -2, 2, 1, 1, 4, 5],
  Acoustic:      [4, 4, 2, 0, 0, 1, 2, 3, 3, 2],
};

function AppInner() {
  const [opusSupported] = useState(() => {
    try {
      const a = document.createElement('audio');
      return a.canPlayType('audio/ogg; codecs=opus') !== '';
    } catch { return true; }
  });
  const [showJester, setShowJester] = useState(false);
  const [showGate, setShowGate] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const [session, setSession] = useState(null);
  const [totpPendingUserId, setTotpPendingUserId] = useState(null);
  const [verifyEmailAddr, setVerifyEmailAddr] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  // 2026-08-20: wake-the-server gate for RETURNING sessions. LoginForm already
  // wakes/waits on a fresh login, but a browser with a stored session token
  // skips LoginForm entirely -- it went straight to /api/user/me against a
  // sleeping server, failed silently, and dumped a logged-in user at the login
  // screen (or an empty app) with no wake ever sent. Same bug existed on
  // Android (LoginActivity's already-logged-in fast path). Mirrors LoginForm's
  // waking/wakeStatus pattern.
  const [waking, setWaking] = useState(false);
  const [wakeStatus, setWakeStatus] = useState('WAKING SERVER...');

  // ── ADULT cache-warm poller (app-wide; survives navigation) ──
  // AdultPage registers job_ids it gets back from search/new-releases/recommended here
  // instead of polling locally itself -- a page-local poll dies the instant the user
  // navigates away, which defeats the "notify anywhere" requirement. adultJobsRef is a
  // ref (not state) deliberately: the poll interval below reads it fresh every 5s tick,
  // so registering a new job never needs to restart the interval or force a re-render.
  const adultJobsRef = useRef({}); // job_id -> title
  const [adultReadyItem, setAdultReadyItem] = useState(null); // {title, streamUrl} once ready
  const [adultReadyPlayer, setAdultReadyPlayer] = useState(null); // {url, title} while playing from the modal

  const registerAdultJob = useCallback((jobId, title) => {
    if (!jobId || adultJobsRef.current[jobId]) return;
    adultJobsRef.current[jobId] = title;
  }, []);

  useEffect(() => {
    if (!session) return;
    const poll = () => {
      const entries = Object.entries(adultJobsRef.current);
      entries.forEach(([jobId, title]) => {
        getAdultCacheStatus(jobId).then((data) => {
          if (!data) return;
          if (data.status === 'ready' && data.stream_url) {
            delete adultJobsRef.current[jobId];
            setAdultReadyItem({ title: data.title || title, streamUrl: data.stream_url, infoHash: data.info_hash || jobId });
          } else if (data.status === 'error') {
            delete adultJobsRef.current[jobId];
          }
        }).catch(() => {});
      });
    };
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [session]);

  // ── Pi heartbeat (app-wide; keeps the server awake while in use) ──
  // power_api.py's monitor_loop suspends the server 120s after the last
  // heartbeat it saw. wakeServer() (LoginForm) only sends one at login --
  // without this, the Pi has no way to know the app is still open, and
  // falls back to Ubuntu-level checks (active terminal login, CPU load, a
  // watched process) that miss the common case of quietly watching a
  // direct-play stream. 30s matches power_api.py's own
  // HEARTBEAT_INTERVAL_SECONDS, well inside its 120s timeout.
  useEffect(() => {
    if (!session) return;
    sendHeartbeat();
    const id = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(id);
  }, [session]);

  // ── Resume-time wake for an ALREADY-OPEN tab (2026-08-20, late) ──
  // The mount-time wake gate below only runs on page load. A tab left open on the
  // dashboard while the server went to sleep gets no reload and therefore no wake
  // -- switching back to it just showed dead content forever (same resume-vs-cold-
  // start gap as Android's ServerWakeGuard, found the same night). When the tab
  // becomes visible again and the server is down: wake it, show the same themed
  // waking screen, then reload the page so everything refetches cleanly.
  const visWakeBusyRef = useRef(false);
  useEffect(() => {
    if (!session) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || visWakeBusyRef.current) return;
      (async () => {
        if (await checkServerAwake()) return;
        visWakeBusyRef.current = true;
        wakeServer();
        // No 800ms grace here (unlike the mount gate) -- we already KNOW the
        // server is down, so show the waking screen immediately.
        setWaking(true);
        const ok = await waitForServerAwake({
          intervalMs: 1500,
          timeoutMs: 90000,
          onTick: (elapsedMs) => {
            if (elapsedMs > 20000) setWakeStatus('STILL BOOTING CORE...');
            if (elapsedMs > 45000) setWakeStatus('TAKING LONGER THAN USUAL...');
          },
        });
        setWaking(false);
        visWakeBusyRef.current = false;
        if (ok) window.location.reload();
      })();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [session]);

  // On mount: check for existing session token and restore if valid.
  // Session handoff for the TV/phone app's embedded admin WebView: the app opens
  // /?session_token=<its token>&screen=admin-panel — adopt the token (it's the same
  // Redis session the API already trusts), scrub it from the address bar, and land
  // directly on the requested screen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const handoffToken = params.get('session_token');
    const handoffScreen = params.get('screen');
    if (handoffToken) {
      localStorage.setItem('aetheris_session_token', handoffToken);
      window.history.replaceState({}, '', window.location.pathname);
    }
    // App-embedded REGISTRATION: ?screen=signup needs no session — skip the gate and
    // splash and land straight on the signup form. On success, onSignedUp hands the
    // fresh session token back to the app via the AetherisApp bridge.
    if (handoffScreen === 'signup') {
      setShowGate(false);
      setShowSplash(false);
      replaceScreen({ type: 'signup' });
    }
    const token = localStorage.getItem('aetheris_session_token');
    if (!token) { setSessionChecked(true); return; }
    (async () => {
      // Wake gate before restoring the session (see waking state's comment).
      // A warm server passes checkServerAwake() on the first try and never
      // shows the splash; a sleeping one gets the wake signal + themed wait,
      // same thresholds as LoginForm. If it never comes up, fall through --
      // the /api/user/me fetch fails like before and lands on the login
      // screen, where LoginForm's own wake flow offers a second chance.
      if (!(await checkServerAwake())) {
        wakeServer();
        await waitForServerAwake({
          intervalMs: 1500,
          timeoutMs: 90000,
          onTick: (elapsedMs) => {
            if (elapsedMs > 800) setWaking(true);
            if (elapsedMs > 20000) setWakeStatus('STILL BOOTING CORE...');
            if (elapsedMs > 45000) setWakeStatus('TAKING LONGER THAN USUAL...');
          },
        });
        setWaking(false);
      }
      fetch('/api/user/me', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.id) {
          setSession({ ...data, session_token: token });
          setShowSplash(false);
          if (handoffScreen === 'admin-panel' && data.is_admin) {
            replaceScreen({ type: 'admin-panel' });
          } else if (handoffScreen === 'account') {
            replaceScreen({ type: 'account' });
          } else {
            replaceScreen({ type: 'dashboard' });
          }
        }
      })
      .catch(() => {})
      .finally(() => setSessionChecked(true));
    })();
  }, []);

  // Core internal tracking engine for router locations
  const [screen, setScreen] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('token')) return { type: 'reset-password' };
    return { type: 'login' };
  });

  // Structural side-effect hook: Intercepts screen mutations and transforms ambient audio tracking loops
  useEffect(() => {
    if (showSplash) {
      transitionSoundscape('splash');
      return;
    }

    // Determine target soundscape environment key based on screen layout type
    let trackKey = 'dashboard';
    
    if (screen.type === 'movies-browse' || screen.type === 'library-browse' || screen.type === 'movie' || screen.type === 'actor') {
      trackKey = 'movies';
    } else if (screen.type === 'admin-downloads') {
      trackKey = 'admin'; // Link "The Stripper" to the glitchy digital matrix hum
    }

    // Check if an interactive movie playback node container is actively present in DOM to prevent overlay bleed
    const isVideoRendering = document.querySelector('video');
    if (isVideoRendering) {
      // audio muted post-login
    } else {
      // audio muted post-login
    }
  }, [screen.type, showSplash]);

  // Secondary effect observer to auto-mute theme tones the split-second a media stream is launched
  useEffect(() => {
    const mediaObserver = new MutationObserver(() => {
      const isVideoRendering = document.querySelector('video');
      if (isVideoRendering) {
        transitionSoundscape('mute_void');
      }
    });

    mediaObserver.observe(document.body, { childList: true, subtree: true });
    return () => mediaObserver.disconnect();
  }, []);

  const handleStartAmbientDrone = useCallback(() => {
    // Left empty since our automatic audioEngine now triggers on implicit navigation/gesture hooks
  }, []);

  // ── GLOBAL MUSIC PLAYER ──────────────────────────────────────────
  const [musicQueue, setMusicQueue] = useState([]);
  const [musicIdx, setMusicIdx] = useState(0);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicTime, setMusicTime] = useState(0);
  const [musicDuration, setMusicDuration] = useState(0);
  // 2026-08-29 owner request: removed the CC on/off toggle -- lyrics now always print in
  // their own panel on the left side of the player (see the lyrics panel render below).
  // lyricsData is null while nothing's loaded yet, {found:false} for a genuine
  // no-lyrics-found, {instrumental:true} for a known instrumental, {synced:true,
  // lines:[...]} or {synced:false, plain:"..."} otherwise.
  const [lyricsData, setLyricsData] = useState(null);
  // 2026-08-29 owner follow-up: lyrics should default open when the player is
  // ENLARGED (there's room) and default closed when it's small, tracked by a
  // useEffect below that watches playerEnlarged. The tab button (toggleLyricsPanel)
  // still overrides that within the current size -- "a way for the lyrics to be
  // closed in large mode" and, conversely, a way to pop them out even in small mode.
  const [lyricsPanelOpen, setLyricsPanelOpen] = useState(false);
  const toggleLyricsPanel = () => setLyricsPanelOpen(v => !v);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  // 2026-08-28 owner request: a menu button that shows the rest of the current track's
  // album as a clickable list, so switching songs doesn't mean leaving the player to go
  // find the album page again.
  const [showAlbumTracks, setShowAlbumTracks] = useState(false);
  const [albumTrackNames, setAlbumTrackNames] = useState(null);
  const [albumTracksLoading, setAlbumTracksLoading] = useState(false);
  const [resolvingTrackName, setResolvingTrackName] = useState(null);
  // 2026-08-29 owner request: a search bar right in the player so the user can play any
  // song without leaving it -- separate from the album-tracks panel above (that's scoped
  // to the current track's own album; this searches everything).
  const [playerSearchOpen, setPlayerSearchOpen] = useState(false);
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [playerSearchResults, setPlayerSearchResults] = useState(null);
  const [playerSearching, setPlayerSearching] = useState(false);
  const playerSearchJobRef = useRef(null);
  // 2026-08-22 (owner request): moved off the bottom-center auto-show/auto-hide-on-any-
  // activity behavior -- now a fixed right-edge tab that opens/closes on click, staying
  // open until the user closes it (no more sliding away mid-listen just because the mouse
  // moved). playerVisible now means "the panel is open", full stop -- toggled explicitly by
  // clicking the tab or the panel's own CLOSE button, not by any window activity listener.
  const [playerVisible, setPlayerVisible] = useState(false);
  // 2026-08-28 owner request: the player panel can now be dragged anywhere, minimized to
  // a slim title bar, and enlarged. playerPos null = the original right-edge docked
  // position (unchanged default); once dragged, it's a real {x, y} viewport position and
  // stays there until the panel is closed/reopened. playerDragRef holds in-flight drag
  // math (not state -- updating state on every mousemove would be a lot of re-renders for
  // no visible benefit over a ref-driven position write).
  const [playerPos, setPlayerPos] = useState(null);
  const [playerMinimized, setPlayerMinimized] = useState(false);
  const [playerEnlarged, setPlayerEnlarged] = useState(false);
  // 2026-08-30 owner request: the small YouTube video popup (isYouTubeTrack, bottom-
  // center) can now enlarge up to full screen -- separate from playerEnlarged above,
  // which only resizes the audio-player CONTROL panel, not this video window.
  const [videoFullscreen, setVideoFullscreen] = useState(false);
  useEffect(() => { setVideoFullscreen(false); }, [musicIdx]);
  // 2026-08-29 owner request: lyrics default open when the player gets enlarged (room
  // for them) and default closed when it shrinks back down -- see lyricsPanelOpen's own
  // comment above for the manual-override half of this.
  useEffect(() => { setLyricsPanelOpen(playerEnlarged); }, [playerEnlarged]);
  // 2026-08-30 owner request: lyrics now auto-collapse the moment the player itself gets
  // minimized -- previously an already-open lyrics panel stayed open (full-size) sitting
  // next to a minimized (slim-bar) player, which looked wrong. The pop-out tab (the small
  // ♪ icon) stays reachable either way -- see hasLyricsContent, which no longer depends
  // on playerMinimized -- so lyrics are still one click away, just closed by default.
  useEffect(() => { if (playerMinimized) setLyricsPanelOpen(false); }, [playerMinimized]);
  const playerDragRef = useRef(null);
  const startPlayerDrag = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const panelEl = e.currentTarget.closest('[data-music-player-panel]');
    const rect = panelEl ? panelEl.getBoundingClientRect() : { left: clientX, top: clientY };
    playerDragRef.current = { offsetX: clientX - rect.left, offsetY: clientY - rect.top };
    const onMove = (ev) => {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      if (!playerDragRef.current) return;
      const w = playerEnlarged ? 360 : 220;
      const h = playerMinimized ? 48 : (playerEnlarged ? 520 : 420);
      const x = Math.min(Math.max(0, cx - playerDragRef.current.offsetX), window.innerWidth - w);
      const y = Math.min(Math.max(0, cy - playerDragRef.current.offsetY), window.innerHeight - h);
      setPlayerPos({ x, y });
    };
    const onUp = () => {
      playerDragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };
  // 2026-08-21 request: explicit AUTOPLAY toggle on the player -- on (default) keeps the
  // existing advance-to-next-track-on-end behavior, off just stops at the end of the current
  // track. Mirrored into a ref because the YT player's onStateChange closure is created once
  // per video and would otherwise read a stale value forever.
  const [musicAutoplay, setMusicAutoplay] = useState(true);
  const musicAutoplayRef = useRef(true);
  useEffect(() => { musicAutoplayRef.current = musicAutoplay; }, [musicAutoplay]);
  const globalAudioRef = useRef(null);

  // 2026-08-30 owner request: EQ with premixed presets. Wired onto the local <audio>
  // element via Web Audio (10-band peaking filter chain) -- only applies to on-drive
  // playback, since YouTube-fallback tracks play through the iframe API, which exposes
  // no audio graph to hook a filter chain into. Filters persist for the whole session;
  // only the MediaElementSourceNode gets recreated each time the <audio> element remounts
  // (it's keyed by track id below, so a fresh element mounts on every track change).
  const [eqPreset, setEqPreset] = useState(() => { try { return localStorage.getItem('aetheris_eq_preset') || 'Flat'; } catch { return 'Flat'; } });
  const [eqOpen, setEqOpen] = useState(false);
  const eqCtxRef = useRef(null);
  const eqFiltersRef = useRef(null);
  const eqSourceElRef = useRef(null);

  const eqApplyPreset = useCallback((name) => {
    const gains = EQ_PRESETS[name] || EQ_PRESETS.Flat;
    if (eqFiltersRef.current) {
      eqFiltersRef.current.forEach((f, i) => { f.gain.value = gains[i] ?? 0; });
    }
  }, []);

  useEffect(() => {
    eqApplyPreset(eqPreset);
    try { localStorage.setItem('aetheris_eq_preset', eqPreset); } catch {}
  }, [eqPreset, eqApplyPreset]);

  // ref callback on the <audio> element -- (re)builds the Web Audio graph the first
  // time, then just reconnects a fresh source node per element after that.
  const attachEq = useCallback((audioEl) => {
    if (!audioEl || eqSourceElRef.current === audioEl) return;
    try {
      if (!eqCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return; // very old browser -- playback still works, just no EQ
        eqCtxRef.current = new AC();
        eqFiltersRef.current = EQ_BANDS.map(freq => {
          const f = eqCtxRef.current.createBiquadFilter();
          f.type = 'peaking'; f.frequency.value = freq; f.Q.value = 1; f.gain.value = 0;
          return f;
        });
        for (let i = 0; i < eqFiltersRef.current.length - 1; i++) {
          eqFiltersRef.current[i].connect(eqFiltersRef.current[i + 1]);
        }
        eqFiltersRef.current[eqFiltersRef.current.length - 1].connect(eqCtxRef.current.destination);
        eqApplyPreset(eqPreset);
      }
      if (eqCtxRef.current.state === 'suspended') eqCtxRef.current.resume().catch(() => {});
      const source = eqCtxRef.current.createMediaElementSource(audioEl);
      source.connect(eqFiltersRef.current[0]);
      eqSourceElRef.current = audioEl;
    } catch {
      // createMediaElementSource throws if this exact element is somehow wired twice
      // (React StrictMode double-invoke, etc) -- non-fatal, audio still plays normally
      // through the element's default output, just without EQ applied.
    }
  }, [eqPreset, eqApplyPreset]);

  // showPlayer just opens the panel -- kept as a named function since several call sites
  // below still call it after an action (e.g. toggling autoplay) to make sure the panel is
  // visibly open when that happens.
  const showPlayer = () => setPlayerVisible(true);

  const currentMusicTrack = musicQueue[musicIdx] || null;
  // Shown as "UP NEXT" in the minimized mini-player (Android's player already surfaces
  // this same way -- see MusicPlayerActivity) since the minimized bar hides the full
  // queue list that's otherwise visible when expanded.
  const nextMusicTrack = musicQueue[musicIdx + 1] || null;
  const isYouTubeTrack = !!(currentMusicTrack && currentMusicTrack.videoId);
  const ytPlayerRef = useRef(null);
  const ytReadyRef = useRef(false);

  // 2026-08-29 owner request: removed the CC toggle -- lyrics now always print in their
  // own panel on the left side of the player instead of being gated behind a button. So
  // this fetch now runs for every track unconditionally, no musicCC gate anymore.
  useEffect(() => {
    if (!currentMusicTrack?.title || !currentMusicTrack?.artist_name) {
      setLyricsData(null);
      return;
    }
    let cancelled = false;
    setLyricsData(null);
    setLyricsLoading(true);
    fetch(`/api/music/lyrics?artist=${encodeURIComponent(currentMusicTrack.artist_name)}&track=${encodeURIComponent(currentMusicTrack.title)}`,
      { headers: { Authorization: 'Bearer ' + (session?.session_token || '') } })
      .then(r => r.ok ? r.json() : { found: false })
      .then(d => { if (!cancelled) setLyricsData(d); })
      .catch(() => { if (!cancelled) setLyricsData({ found: false }); })
      .finally(() => { if (!cancelled) setLyricsLoading(false); });
    return () => { cancelled = true; };
  }, [currentMusicTrack?.title, currentMusicTrack?.artist_name]);

  // Finds the synced lyric line active at the current playback time -- last line whose
  // timestamp has passed. -1 (nothing shown yet) until the first line's timestamp.
  const activeLyricIdx = (() => {
    if (!lyricsData?.synced || !lyricsData.lines?.length) return -1;
    const ms = musicTime * 1000;
    let idx = -1;
    for (let i = 0; i < lyricsData.lines.length; i++) {
      if (lyricsData.lines[i].time_ms <= ms) idx = i; else break;
    }
    return idx;
  })();

  // Loads the current track's album track list when the ALBUM TRACKS panel is opened
  // (and again if the playing album changes while it's open) -- names only, resolved
  // per-track on click rather than upfront, so opening the panel is instant even for a
  // huge album.
  useEffect(() => {
    if (!showAlbumTracks || !currentMusicTrack?.album_name || !currentMusicTrack?.artist_name) {
      if (!showAlbumTracks) setAlbumTrackNames(null);
      return;
    }
    let cancelled = false;
    setAlbumTracksLoading(true);
    fetch(`/api/music/album?artist=${encodeURIComponent(currentMusicTrack.artist_name)}&album=${encodeURIComponent(currentMusicTrack.album_name)}`,
      { headers: { Authorization: 'Bearer ' + (session?.session_token || '') } })
      .then(r => r.ok ? r.json() : { tracks: [] })
      .then(d => { if (!cancelled) setAlbumTrackNames((d.tracks || []).map(t => t.name)); })
      .catch(() => { if (!cancelled) setAlbumTrackNames([]); })
      .finally(() => { if (!cancelled) setAlbumTracksLoading(false); });
    return () => { cancelled = true; };
  }, [showAlbumTracks, currentMusicTrack?.album_name, currentMusicTrack?.artist_name]);

  // Switches playback to a different track from the album list -- on-drive first
  // (instant), else resolves a video for it (same Piped-backed lookup used everywhere
  // else in Music) and plays that, kicking a background rip so it's on-drive next time.
  // Same shape as MusicPage.jsx's resolveAndRip/TrackActionButtons.playAudio, just
  // living here since the album-tracks panel is part of the global player, not a page.
  const playAlbumTrackByName = async (trackName) => {
    if (!currentMusicTrack || resolvingTrackName) return;
    const artist = currentMusicTrack.artist_name;
    const album = currentMusicTrack.album_name;
    const token = session?.session_token || '';
    setResolvingTrackName(trackName);
    try {
      const onDriveR = await fetch(`/api/music/on-drive?artist=${encodeURIComponent(artist)}`, { headers: { Authorization: 'Bearer ' + token } });
      const onDrive = onDriveR.ok ? await onDriveR.json() : [];
      const found = (onDrive || []).find(t => (t.title || '').toLowerCase() === trackName.toLowerCase());
      if (found) {
        musicPlayTrack([{ id: found.id, title: trackName, artist_name: artist, album_name: album }], 0);
        return;
      }
      const vr = await fetch(`/api/music/youtube?artist=${encodeURIComponent(artist)}&track=${encodeURIComponent(trackName)}`, { headers: { Authorization: 'Bearer ' + token } });
      const vd = vr.ok ? await vr.json() : {};
      if (vd.video_id) {
        musicPlayTrack([{ videoId: vd.video_id, title: trackName, artist_name: artist, album_name: album }], 0);
        // Fire-and-forget background rip so this track is on-drive (fast path) next time.
        fetch('/api/music/rip', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify({ video_id: vd.video_id, artist, album: album || 'Unknown Album', title: trackName }) }).catch(() => {});
        return;
      }
      alert(`Couldn't find a source for "${trackName}"`);
    } catch {
      alert(`Couldn't find a source for "${trackName}"`);
    } finally {
      setResolvingTrackName(null);
    }
  };

  // 2026-08-29 owner request: search bar in the player itself. Debounced live search
  // (same /music/search/lastfm endpoint MusicPage.jsx's own search uses), tracks only --
  // artist/album results have no obvious "play" action from inside the player. Clicking a
  // result queues it as a lazy MusicQueueItem and jumps straight to it; the existing
  // musicPrefetch effect (built for PLAY ALL/SHUFFLE) resolves it -- on-drive instantly,
  // or rips it -- with zero extra code needed here.
  const playerRunSearch = (q) => {
    setPlayerSearchQuery(q);
    if (playerSearchJobRef.current) clearTimeout(playerSearchJobRef.current);
    if (!q.trim()) { setPlayerSearchResults(null); setPlayerSearching(false); return; }
    setPlayerSearching(true);
    playerSearchJobRef.current = setTimeout(async () => {
      try {
        const res = await searchMusicLastfm(q);
        setPlayerSearchResults(res?.tracks || []);
      } catch {
        setPlayerSearchResults([]);
      } finally {
        setPlayerSearching(false);
      }
    }, 350);
  };

  const playerPlaySearchResult = (track) => {
    musicPlayTrack([{ title: track.name, artist_name: track.artist, album_name: '' }], 0);
    setPlayerSearchOpen(false);
    setPlayerSearchQuery('');
    setPlayerSearchResults(null);
  };

  const timeIntervalRef = useRef(null);

  // Real YouTube IFrame Player -- creates/destroys a YT.Player targeting the
  // #yt-player-target div below as the current track's videoId changes. Without
  // this, ytPlayerRef was never attached to anything real and every
  // playVideo()/pauseVideo()/seekTo() call below was a silent no-op.
  useEffect(() => {
    if (!isYouTubeTrack || !currentMusicTrack?.videoId) return;
    let cancelled = false;
    const createPlayer = () => {
      if (cancelled) return;
      if (ytPlayerRef.current && ytPlayerRef.current.loadVideoById) {
        ytPlayerRef.current.loadVideoById(currentMusicTrack.videoId);
        ytReadyRef.current = true;
        return;
      }
      ytPlayerRef.current = new window.YT.Player('yt-player-target', {
        videoId: currentMusicTrack.videoId,
        playerVars: { autoplay: 1, controls: 0, disablekb: 1, modestbranding: 1, playsinline: 1 },
        events: {
          onReady: () => { ytReadyRef.current = true; if (musicPlaying) ytPlayerRef.current.playVideo(); },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) { musicOnEnded(); }
            else if (e.data === window.YT.PlayerState.PLAYING) { setMusicPlaying(true); setMusicDuration(ytPlayerRef.current.getDuration() || 0); }
            else if (e.data === window.YT.PlayerState.PAUSED) { setMusicPlaying(false); }
          },
        },
      });
    };
    if (window.YT && window.YT.Player) {
      createPlayer();
    } else {
      const prevReady = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prevReady && prevReady(); createPlayer(); };
      if (!document.getElementById('yt-iframe-api-script')) {
        const tag = document.createElement('script');
        tag.id = 'yt-iframe-api-script';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
    }
    return () => { cancelled = true; };
  }, [currentMusicTrack?.videoId, isYouTubeTrack]);

  useEffect(() => {
    if (!isYouTubeTrack || !ytReadyRef.current) return;
    clearInterval(timeIntervalRef.current);
    timeIntervalRef.current = setInterval(() => {
      if (ytPlayerRef.current?.getCurrentTime) setMusicTime(ytPlayerRef.current.getCurrentTime() || 0);
    }, 500);
    return () => clearInterval(timeIntervalRef.current);
  }, [isYouTubeTrack, currentMusicTrack?.videoId]);


  useEffect(() => {
    if (isYouTubeTrack) return;
    if (!musicPlaying || !currentMusicTrack) return;
    const t = setTimeout(() => {
      if (globalAudioRef.current) globalAudioRef.current.play().catch(() => {});
    }, 100);
    return () => clearTimeout(t);
  }, [musicIdx, musicPlaying, currentMusicTrack, isYouTubeTrack]);

  // 2026-08-29 (owner request: "every track is downloaded, cached, scanned and ready to
  // play next"): PLAY ALL/SHUFFLE now queue every track up front, resolved or not (see
  // MusicPage.jsx's handlePlayAllArtistAudio/handlePlayAlbumAudio/handlePlayAllGenre) --
  // this effect is what actually makes that true instead of just a promise. It resolves
  // (rips if needed) the current track plus a couple tracks ahead, in queue order, so
  // playback never actually has to wait once it gets there. Runs off a ref-tracked
  // in-flight set (not state) so re-renders don't restart an already-running resolve.
  const musicResolvingRef = useRef(new Set()); // keys currently being resolved
  const [musicResolvingCurrent, setMusicResolvingCurrent] = useState(false);
  const MUSIC_PREFETCH_AHEAD = 2;
  useEffect(() => {
    if (!musicQueue.length) return;
    const trackKey = (t) => `${t.artist_name}::${t.title}`;
    const resolveSlot = async (idx) => {
      const t = musicQueue[idx];
      if (!t || t.id || t.videoId) return; // already resolved, or a YouTube track (no rip needed)
      const key = trackKey(t);
      if (musicResolvingRef.current.has(key)) return;
      musicResolvingRef.current.add(key);
      if (idx === musicIdx) setMusicResolvingCurrent(true);
      try {
        const id = await resolveAndRipTrack(t.artist_name, t.album_name, t.title);
        if (id) {
          setMusicQueue(prev => {
            const next = [...prev];
            // guard against the queue having changed shape (new PLAY ALL click) since this
            // resolve started -- only patch if the slot still holds the same track.
            if (next[idx] && trackKey(next[idx]) === key) next[idx] = { ...next[idx], id };
            return next;
          });
        }
      } finally {
        musicResolvingRef.current.delete(key);
        if (idx === musicIdx) setMusicResolvingCurrent(false);
      }
    };
    for (let i = musicIdx; i <= Math.min(musicIdx + MUSIC_PREFETCH_AHEAD, musicQueue.length - 1); i++) {
      resolveSlot(i);
    }
  }, [musicQueue, musicIdx]);

  const musicAppendQueue = (tracks) => {
    setMusicQueue(prev => { const next = [...prev, ...tracks]; if (!prev.length) { setMusicIdx(0); setMusicPlaying(true); } return next; });
  };
  const musicPlayTrack = (tracks, idx) => {
    setMusicQueue(tracks);
    setMusicIdx(idx);
    setMusicPlaying(true);
    showPlayer();
  };

  const musicShuffle = (tracks) => {
    const shuffled = [...tracks].sort(() => Math.random() - 0.5);
    setMusicQueue(shuffled);
    setMusicIdx(0);
    setMusicPlaying(true);
    showPlayer();
  };

  const musicNext = () => {
    if (!musicQueue.length) return;
    const next = (musicIdx + 1) % musicQueue.length;
    setMusicIdx(next);
    setMusicPlaying(true);
  };

  const musicPrev = () => {
    if (!musicQueue.length) return;
    const prev = (musicIdx - 1 + musicQueue.length) % musicQueue.length;
    setMusicIdx(prev);
    setMusicPlaying(true);
  };

  // End-of-track: advance only when the AUTOPLAY toggle is on (reads the ref, not the
  // state -- see musicAutoplayRef's comment). With it off, just stop where the track ended.
  const musicOnEnded = () => {
    if (musicAutoplayRef.current) musicNext();
    else setMusicPlaying(false);
  };

  // 2026-08-21 request: 10-second skip buttons on the player, both directions.
  const musicSkip = (delta) => {
    if (isYouTubeTrack) {
      const cur = ytPlayerRef.current?.getCurrentTime?.() || 0;
      ytPlayerRef.current?.seekTo?.(Math.max(0, cur + delta), true);
    } else if (globalAudioRef.current) {
      globalAudioRef.current.currentTime = Math.max(0, (globalAudioRef.current.currentTime || 0) + delta);
    }
    showPlayer();
  };

  const musicTogglePlay = () => {
    if (isYouTubeTrack) {
      if (musicPlaying) { ytPlayerRef.current?.pauseVideo(); setMusicPlaying(false); }
      else { ytPlayerRef.current?.playVideo(); setMusicPlaying(true); }
    } else {
      if (globalAudioRef.current) {
        if (musicPlaying) globalAudioRef.current.pause();
        else globalAudioRef.current.play().catch(() => {});
      }
    }
  };

  const musicStop = () => {
    if (globalAudioRef.current) globalAudioRef.current.pause();
    if (ytPlayerRef.current) ytPlayerRef.current.stopVideo();
    clearInterval(timeIntervalRef.current);
    setMusicQueue([]); setMusicIdx(0); setMusicPlaying(false);
    setPlayerVisible(false); setMusicTime(0); setMusicDuration(0);
  };

  const fmtMusicTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };

  const pushScreen = useCallback((newScreen) => {
    window.history.pushState(newScreen, '', '');
    setScreen(newScreen);
  }, []);

  // Parental-controls route guard (2026-08-09) -- checked EVERY time, not just once per
  // session, per spec. navigateGuarded is what SideNav's onNavigate calls instead of
  // pushScreen directly; if the target maps to a locked section it opens a password
  // prompt first and only pushes the screen once verifyParentalPassword confirms it.
  const [parentalGate, setParentalGate] = useState(null); // null | { key }
  const [parentalGatePw, setParentalGatePw] = useState('');
  const [parentalGateErr, setParentalGateErr] = useState('');
  const [parentalGateBusy, setParentalGateBusy] = useState(false);

  const navigateGuarded = useCallback((key) => {
    const section = PARENTAL_SECTION_MAP[key];
    // 2026-08-29 owner request: master on/off switch -- OFF means nothing is enforced
    // regardless of which sections are checked (session.parental_enabled defaults
    // true server-side, so an older cached session with no field yet still enforces).
    const locked = section && session && session.parental_enabled !== false
      && (session.parental_locked_sections || []).includes(section);
    if (locked) {
      setParentalGate({ key });
      setParentalGatePw(''); setParentalGateErr('');
      return;
    }
    pushScreen({ type: key });
  }, [session, pushScreen]);

  const submitParentalGate = () => {
    if (!parentalGatePw) { setParentalGateErr('Enter the parental password.'); return; }
    setParentalGateBusy(true);
    verifyParentalPassword(parentalGatePw)
      .then(res => {
        if (res && res.ok) {
          const target = parentalGate.key;
          setParentalGate(null); setParentalGatePw('');
          pushScreen({ type: target });
        } else {
          setParentalGateErr('Incorrect password.');
        }
      })
      .catch(() => setParentalGateErr('Network error.'))
      .finally(() => setParentalGateBusy(false));
  };

  const replaceScreen = useCallback((newScreen) => {
    window.history.replaceState(newScreen, '', '');
    setScreen(newScreen);
  }, []);

  const popScreen = useCallback(() => {
    window.history.back();
  }, []);

  // 2026-08-30 fix ("back always goes home"): this used to be two separate mount-time
  // effects -- the first seeded the current history entry with {type:'login'}, and the
  // second UNCONDITIONALLY replaced it again with null right after, wiping out real
  // screen state. Harmless on a cold load (nothing real to lose yet), but AppInner
  // remounts more often than that -- the stale-chunk auto-reload in main.jsx, and the
  // explicit reloads this file itself does on various auth transitions, all remount this
  // component while potentially deep in the screen stack. Each remount re-ran both
  // effects and stomped whatever the CURRENT entry's real state was to null, so the next
  // back press landed on the {type:'login'} fallback instead of the actual previous
  // screen. Fixed by only seeding state if the current entry genuinely has none yet
  // (a true first load), and dropping the unconditional null-out entirely.
  useEffect(() => {
    if (!window.history.state) {
      window.history.replaceState({ type: 'login' }, '', '');
    }
    const handlePopState = (event) => {
      setScreen(event.state || { type: 'login' });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const [showScrollTop, setShowScrollTop] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Server-waking splash for returning sessions (see the waking state's
  // comment) -- takes precedence over the gate/splash so the wake progress is
  // visible immediately on load, same screen LoginForm shows on fresh logins.
  if (waking) {
    return <ServerWakingScreen statusText={wakeStatus} />;
  }

  if (showGate) {
    return (
      <>
        <PowerOutageBanner />
        <EnterGate onEnter={() => setShowGate(false)} />
      </>
    );
  }

  if (showSplash) {
    return (
      <>
        <PowerOutageBanner />
        <AetherisSplashAuth
          onComplete={() => setShowSplash(false)}
          onEngageAudio={handleStartAmbientDrone}
          muted={false}
        />
      </>
    );
  }

  return (
    <>
      <PowerOutageBanner />
      {/* 2026-08-16 request: "when a user gets a message from another user make a popup
          notice ... no matter where they are" -- mounted here (the authenticated branch
          only, unlike PowerOutageBanner which also covers the pre-login gate/splash screens)
          since notifications require a session to exist at all. */}
      <MessagePopupNotice onOpenMessages={() => pushScreen({ type: 'messages' })} />
      <div style={{ position: 'fixed', bottom: '12px', right: '16px', fontFamily: 'Orbitron, monospace', fontSize: '9px', color: '#2a2a3a', letterSpacing: '0.15em', userSelect: 'none', zIndex: 9999, pointerEvents: 'none' }}>
        AETHERIS v3.0.24
      </div>
      {/* 2026-08-27: home/back/forward/refresh moved INTO SideNav (see its own comment) --
          was a floating top-center bar here, always covering page content; now it hides/
          shows with the sidenav's existing collapse/hover behavior instead. */}
      {showScrollTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{ position: 'fixed', bottom: '32px', right: '32px', zIndex: 9997, width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(176,38,255,0.15)', border: '1px solid rgba(176,38,255,0.5)', color: '#b026ff', fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 16px rgba(176,38,255,0.3)', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(176,38,255,0.3)'; e.currentTarget.style.boxShadow = '0 0 24px rgba(176,38,255,0.6)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(176,38,255,0.15)'; e.currentTarget.style.boxShadow = '0 0 16px rgba(176,38,255,0.3)'; }}>
          ↑
        </button>
      )}

      {/* ── GLOBAL MUSIC PLAYER ── */}
      {/* No more bottom-dock dodge -- the controls live on the left edge now, so the
          video window can just sit at the bottom center permanently. */}
      {isYouTubeTrack && (
        <div style={videoFullscreen ? {
          position: 'fixed', inset: 0, width: '100vw', height: '100vh', zIndex: 1200,
          borderRadius: 0, overflow: 'hidden', border: 'none', boxShadow: 'none', background: '#000',
        } : {
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', width: '320px', height: '180px', zIndex: 999, borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(155,48,255,0.4)', boxShadow: '0 8px 32px rgba(0,0,0,0.7)', background: '#000',
        }}>
          <div id="yt-player-target" style={{ width: '100%', height: '100%' }} />
          {/* 2026-08-30 owner request: enlarge the video popup up to full screen. */}
          <button onClick={() => setVideoFullscreen(v => !v)} title={videoFullscreen ? 'Exit full screen' : 'Full screen'} style={{
            position: 'absolute', top: '8px', right: '8px', width: '28px', height: '28px', borderRadius: '6px',
            background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
            fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
          }}>
            {videoFullscreen ? '⤡' : '⤢'}
          </button>
        </div>
      )}

      {currentMusicTrack && !isYouTubeTrack && currentMusicTrack.id && (
        <audio
          key={currentMusicTrack.id}
          ref={node => { globalAudioRef.current = node; attachEq(node); }}
          crossOrigin="anonymous"
          src={currentMusicTrack.streamUrl || (opusSupported ? `/api/music/stream/${currentMusicTrack.id}?token=${session?.session_token}` : `/api/music/stream/${currentMusicTrack.id}/transcode?fmt=aac&token=${session?.session_token}`)}
          autoPlay={musicPlaying}
          onPlay={() => { setMusicPlaying(true); if (!opusSupported) { setShowJester(true); } }}
          onCanPlayThrough={() => { if (window._jesterDone) window._jesterDone(); }}
          onPause={() => setMusicPlaying(false)}
          onEnded={musicOnEnded}
          onTimeUpdate={() => setMusicTime(globalAudioRef.current?.currentTime || 0)}
          onDurationChange={() => setMusicDuration(globalAudioRef.current?.duration || 0)}
          onCanPlay={() => { if (musicPlaying && globalAudioRef.current) globalAudioRef.current.play().catch(() => {}); }}
          style={{ display: 'none' }}
        />
      )}
      {/* 2026-08-21: first pass pinned this to the left edge like a sidenav, but that's
          exactly where the site's REAL SideNav lives (SideNav.jsx: fixed, left:0, top:0,
          bottom:0, full height) -- the two overlapped and the player panel sat on top of
          real navigation. Moved to the bottom, then (2026-08-22 request) moved again to a
          right-edge tab that opens/closes on click instead of sliding in on any window
          activity and auto-hiding after 3s -- stays open until the user closes it. */}
      {currentMusicTrack && (
        <button onClick={() => setPlayerVisible(v => !v)} style={{
          position: 'fixed', right: 0, top: '50%', transform: `translateY(-50%) translateX(${playerVisible ? '220px' : '0'})`,
          zIndex: 1001, background: 'rgba(155,48,255,0.9)', border: '1px solid rgba(155,48,255,0.6)',
          borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px', borderRight: 'none',
          padding: '14px 8px', cursor: 'pointer', color: '#fff', fontSize: '11px',
          fontFamily: 'Orbitron, monospace', letterSpacing: '0.06em', writingMode: 'vertical-rl',
          transition: 'transform 0.3s ease', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
          {musicPlaying ? '♪ PLAYING' : '♪ PAUSED'}
        </button>
      )}
      {currentMusicTrack && (() => {
        // 2026-08-28 owner request: the player is now a real popup window -- draggable
        // by its title bar (startPlayerDrag, see its own comment), minimizable to a
        // slim bar, and enlargeable. playerPos null keeps the original right-edge
        // docked behavior (slide in/out via the tab button) exactly as before; once
        // dragged even once, it becomes a free-floating window at an explicit {x,y}
        // and the tab button just toggles it hidden/shown in place instead of sliding.
        const width = playerEnlarged ? 360 : 220;
        const dockedStyle = {
          right: 0, top: '50%',
          transform: `translateY(-50%) translateX(${playerVisible ? '0' : '100%'})`,
        };
        const floatingStyle = playerPos ? {
          left: `${playerPos.x}px`, top: `${playerPos.y}px`,
          transform: 'none',
        } : dockedStyle;
        // 2026-08-29 owner request: lyrics always fetch (no more CC toggle), but the
        // panel itself pops out/collapses instead of permanently eating screen space.
        // Same shape as the player's own docked-edge tab (playerVisible): a small
        // always-there tab toggles a full panel that's only ever mounted while open.
        // Collapsed state persists (see lyricsPanelOpen above). The panel itself is only
        // rendered once there's something worth printing (found lyrics, or a definite
        // instrumental/no-lyrics state) so an empty box never flashes up before fetch.
        const lyricsWidth = 260;
        // 2026-08-30 owner request: lyrics are now reachable (and hideable via the same
        // pop-out tab) even while the player itself is minimized -- previously this was
        // gated on !playerMinimized, so lyrics were unconditionally unavailable the moment
        // you shrank the player, with no way to peek at them without fully restoring it.
        const hasLyricsContent = lyricsLoading || lyricsData;
        const lyricsStyle = playerPos
          ? { left: `${playerPos.x - lyricsWidth - 10}px`, top: `${playerPos.y}px`, transform: 'none' }
          : { right: `${width + 10}px`, top: '50%', transform: `translateY(-50%) translateX(${playerVisible ? '0' : '100%'})` };
        const lyricsTabStyle = playerPos
          ? { left: `${playerPos.x - 34}px`, top: `${playerPos.y}px` }
          : { right: `${width + 6}px`, top: '50%', transform: `translateY(-50%) translateX(${playerVisible ? '0' : '100%'})` };
        return (
          <>
          {hasLyricsContent && (
            <button onClick={toggleLyricsPanel} title={lyricsPanelOpen ? 'Collapse lyrics' : 'Show lyrics'} style={{
              position: 'fixed', ...lyricsTabStyle, zIndex: 999,
              background: 'rgba(13,8,20,0.95)', border: '1px solid rgba(155,48,255,0.4)',
              borderRadius: '8px', width: '26px', height: '40px', cursor: 'pointer',
              color: '#cc88ff', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0, opacity: playerVisible ? 1 : 0, pointerEvents: playerVisible ? 'auto' : 'none',
              transition: 'opacity 0.2s ease', boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
            }}>{lyricsPanelOpen ? '▸' : '♪'}</button>
          )}
          {hasLyricsContent && lyricsPanelOpen && (
            <div style={{
              position: 'fixed', ...lyricsStyle,
              width: `${lyricsWidth}px`, maxHeight: 'calc(100vh - 48px)', overflowY: 'auto',
              background: 'rgba(7,5,15,0.97)', border: '1px solid rgba(155,48,255,0.35)',
              borderRadius: '14px', padding: '16px 14px',
              zIndex: 999, backdropFilter: 'blur(12px)',
              opacity: playerVisible ? 1 : 0,
              transition: playerPos ? 'opacity 0.2s ease' : 'opacity 0.3s ease, transform 0.3s ease',
              pointerEvents: playerVisible ? 'auto' : 'none',
              boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 20px rgba(155,48,255,0.1)',
              fontFamily: 'Rajdhani, sans-serif',
            }}>
              {/* 2026-08-30 owner request: an explicit close control for just the lyrics
                  panel (previously only the pop-out tab toggled it -- same action, but
                  not obviously a "close" from inside the panel itself). */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.12em', color: '#7a5a9a' }}>♪ LYRICS</div>
                <button onClick={() => setLyricsPanelOpen(false)} title="Close lyrics" style={{
                  background: 'transparent', border: 'none', color: '#7a5a9a', cursor: 'pointer',
                  fontSize: '13px', padding: '0 2px', lineHeight: 1,
                }}>✕</button>
              </div>
              {lyricsLoading ? (
                <div style={{ color: '#5a5a7a', fontSize: '11px', fontFamily: 'monospace' }}>[ FINDING LYRICS... ]</div>
              ) : !lyricsData?.found ? (
                <div style={{ color: '#5a5a7a', fontSize: '11px', fontFamily: 'monospace' }}>[ NO LYRICS FOUND ]</div>
              ) : lyricsData.instrumental ? (
                <div style={{ color: '#5a5a7a', fontSize: '11px', fontFamily: 'monospace' }}>[ INSTRUMENTAL ]</div>
              ) : lyricsData.synced ? (
                <div>
                  {lyricsData.lines.map((line, i) => (
                    <div key={i} style={{
                      fontSize: i === activeLyricIdx ? '13px' : '12px',
                      fontWeight: i === activeLyricIdx ? '700' : '400',
                      color: i === activeLyricIdx ? '#fff' : 'rgba(224,192,255,0.55)',
                      padding: '2px 0', transition: 'all 150ms',
                    }}>{line.text || '♪'}</div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'rgba(224,192,255,0.7)', fontSize: '12px', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                  {lyricsData.plain}
                </div>
              )}
            </div>
          )}
          <div data-music-player-panel style={{
            position: 'fixed', ...floatingStyle,
            width: `${width}px`,
            maxHeight: playerMinimized ? (nextMusicTrack ? '66px' : '48px') : 'calc(100vh - 48px)',
            overflowY: playerMinimized ? 'hidden' : 'auto',
            background: 'rgba(7,5,15,0.97)', border: '1px solid rgba(155,48,255,0.35)',
            borderRight: playerPos ? '1px solid rgba(155,48,255,0.35)' : 'none',
            borderRadius: playerPos ? '14px' : '14px 0 0 14px',
            padding: playerMinimized ? '8px 10px' : '16px 14px',
            display: 'flex', flexDirection: 'column', gap: '10px',
            zIndex: 1000, backdropFilter: 'blur(12px)',
            opacity: playerVisible ? 1 : 0,
            transition: playerPos ? 'opacity 0.2s ease' : 'opacity 0.3s ease, transform 0.3s ease',
            pointerEvents: playerVisible ? 'auto' : 'none',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 20px rgba(155,48,255,0.1)',
          }}>
            {/* Title bar -- drag handle + minimize/enlarge/restore controls. Grabbing
                anywhere on this row (except the buttons themselves) starts a drag. */}
            <div
              onMouseDown={startPlayerDrag}
              onTouchStart={startPlayerDrag}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'grab', flexShrink: 0 }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentMusicTrack.title}
                </div>
                {!playerMinimized && (
                  <div style={{ fontSize: '11px', color: '#5a5a7a', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {currentMusicTrack.artist_name}
                    {isYouTubeTrack && <span style={{ marginLeft: '8px', color: '#ff4444', fontSize: '9px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.1em' }}>YT</span>}
                    {!isYouTubeTrack && !currentMusicTrack.id && musicResolvingCurrent && <span style={{ marginLeft: '8px', color: '#00ff88', fontSize: '9px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.1em' }}>LOCATING SOURCE...</span>}
                  </div>
                )}
              </div>
              <button
                onClick={() => setPlayerMinimized(m => !m)}
                title={playerMinimized ? 'Restore' : 'Minimize'}
                style={{ background: 'transparent', border: '1px solid rgba(155,48,255,0.3)', color: '#cc88ff', width: '22px', height: '22px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {playerMinimized ? '▢' : '—'}
              </button>
              {!playerMinimized && (
                <button
                  onClick={() => setPlayerEnlarged(e => !e)}
                  title={playerEnlarged ? 'Shrink' : 'Enlarge'}
                  style={{ background: 'transparent', border: '1px solid rgba(155,48,255,0.3)', color: '#cc88ff', width: '22px', height: '22px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {playerEnlarged ? '⤡' : '⤢'}
                </button>
              )}
              {playerMinimized && (
                <button
                  onClick={musicTogglePlay}
                  title={musicPlaying ? 'Pause' : 'Play'}
                  style={{ background: 'rgba(155,48,255,0.2)', border: '1px solid rgba(155,48,255,0.4)', color: '#cc88ff', width: '22px', height: '22px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  {musicPlaying ? '⏸' : '▶'}
                </button>
              )}
            </div>
            {playerMinimized && nextMusicTrack && (
              <div style={{ fontSize: '9px', color: '#5a5a7a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '-6px' }}>
                <span style={{ color: '#7a5a9a', fontFamily: 'Orbitron, monospace', letterSpacing: '0.06em' }}>UP NEXT: </span>
                {nextMusicTrack.title}
              </div>
            )}

            {!playerMinimized && (
              <>
                {/* 2026-08-27 owner request: album on its own line below the artist name,
                    mirrors the Android app's audio player (was inline "Artist · Album" before). */}
                {currentMusicTrack.album_name && (
                  <div style={{ fontSize: '10px', color: '#4a3a6a', marginTop: '-6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {currentMusicTrack.album_name}
                  </div>
                )}

                {/* Seek bar + times */}
                <div>
                  <div style={{ height: '4px', background: '#1a1a2e', borderRadius: '2px', cursor: 'pointer' }}
                    onClick={e => {
                      if (!musicDuration) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pct = (e.clientX - rect.left) / rect.width;
                      if (isYouTubeTrack) { ytPlayerRef.current?.seekTo(pct * musicDuration, true); }
                      else if (globalAudioRef.current) { globalAudioRef.current.currentTime = pct * musicDuration; }
                    }}>
                    <div style={{ height: '100%', width: `${musicDuration ? (musicTime / musicDuration * 100) : 0}%`, background: 'linear-gradient(90deg, #9b30ff, #cc88ff)', borderRadius: '2px' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                    <span style={{ fontSize: '10px', color: '#3a3a5a', fontFamily: 'monospace' }}>{fmtMusicTime(musicTime)}</span>
                    <span style={{ fontSize: '10px', color: '#3a3a5a', fontFamily: 'monospace' }}>{fmtMusicTime(musicDuration)}</span>
                  </div>
                </div>

                {/* 2026-08-30 owner request: show the up-next track right above the
                    transport controls in the expanded player too (the minimized bar
                    already surfaces this -- see nextMusicTrack above). */}
                {nextMusicTrack && (
                  <div style={{ fontSize: '10px', color: '#5a5a7a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ color: '#7a5a9a', fontFamily: 'Orbitron, monospace', letterSpacing: '0.08em' }}>UP NEXT: </span>
                    {nextMusicTrack.title}
                  </div>
                )}

                {/* Vertically stacked controls -- prev, next, 10s both ways, play, autoplay. */}
                {[
                  ['⏮  PREV', musicPrev],
                  ['⏭  NEXT', musicNext],
                  ['⟲  10s BACK', () => musicSkip(-10)],
                  ['⟳  10s AHEAD', () => musicSkip(10)],
                  [musicPlaying ? '⏸  PAUSE' : '▶  PLAY', musicTogglePlay],
                ].map(([label, fn], i) => (
                  <button key={i} onClick={fn} style={{
                    background: 'rgba(155,48,255,0.12)', border: '1px solid rgba(155,48,255,0.3)',
                    borderRadius: '8px', width: '100%', minHeight: playerEnlarged ? '48px' : '40px', cursor: 'pointer',
                    color: '#cc88ff', fontSize: playerEnlarged ? '14px' : '12px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.08em',
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0 12px', gap: '8px',
                  }}>{label}</button>
                ))}
                <button onClick={() => { setMusicAutoplay(a => !a); showPlayer(); }} style={{
                  background: musicAutoplay ? 'rgba(155,48,255,0.3)' : 'rgba(155,48,255,0.06)',
                  border: `1px solid ${musicAutoplay ? '#9b30ff' : 'rgba(155,48,255,0.2)'}`,
                  borderRadius: '8px', width: '100%', minHeight: '40px', cursor: 'pointer',
                  color: musicAutoplay ? '#e0c0ff' : '#5a5a7a', fontSize: '12px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.08em',
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0 12px', gap: '8px',
                }}>⟳  AUTOPLAY {musicAutoplay ? 'ON' : 'OFF'}</button>
                {/* 2026-08-30 owner request: EQ with premixed presets. Only actually
                    affects on-drive playback (the Web Audio graph is wired onto the
                    <audio> element above) -- flagged when the current track is a
                    YouTube fallback, since that plays through the iframe API instead. */}
                <button onClick={() => setEqOpen(v => !v)} style={{
                  background: eqOpen ? 'rgba(155,48,255,0.3)' : (eqPreset !== 'Flat' ? 'rgba(155,48,255,0.18)' : 'rgba(155,48,255,0.06)'),
                  border: `1px solid ${eqOpen || eqPreset !== 'Flat' ? '#9b30ff' : 'rgba(155,48,255,0.2)'}`,
                  borderRadius: '8px', width: '100%', minHeight: '40px', cursor: 'pointer',
                  color: eqOpen || eqPreset !== 'Flat' ? '#e0c0ff' : '#5a5a7a', fontSize: '12px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.08em',
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0 12px', gap: '8px',
                }}>▤  EQUALIZER: {eqPreset.toUpperCase()}</button>
                {eqOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(155,48,255,0.15)', borderRadius: '8px', padding: '6px' }}>
                    {isYouTubeTrack && (
                      <div style={{ fontSize: '9px', color: '#7a5a9a', fontFamily: 'monospace', padding: '2px 4px 4px' }}>
                        [ NOTE: doesn't apply to this YouTube-sourced track ]
                      </div>
                    )}
                    {Object.keys(EQ_PRESETS).map(name => (
                      <button key={name} onClick={() => setEqPreset(name)} style={{
                        background: eqPreset === name ? 'rgba(155,48,255,0.3)' : 'transparent',
                        border: 'none', borderRadius: '5px', padding: '7px 8px', cursor: 'pointer',
                        color: eqPreset === name ? '#fff' : 'rgba(224,192,255,0.75)',
                        fontSize: '12px', fontFamily: 'Rajdhani, sans-serif', fontWeight: eqPreset === name ? '700' : '500',
                        textAlign: 'left',
                      }}
                        onMouseEnter={e => { if (eqPreset !== name) e.currentTarget.style.background = 'rgba(155,48,255,0.1)'; }}
                        onMouseLeave={e => { if (eqPreset !== name) e.currentTarget.style.background = 'transparent'; }}>
                        {eqPreset === name ? '▶ ' : ''}{name}
                      </button>
                    ))}
                  </div>
                )}
                {/* 2026-08-28 owner request: a button that shows the rest of the current
                    track's album as a clickable list, to switch songs without leaving
                    the player. Only makes sense when the current track actually has a
                    known album. */}
                {currentMusicTrack.album_name && (
                  <button onClick={() => setShowAlbumTracks(v => !v)} style={{
                    background: showAlbumTracks ? 'rgba(155,48,255,0.3)' : 'rgba(155,48,255,0.12)',
                    border: `1px solid ${showAlbumTracks ? '#9b30ff' : 'rgba(155,48,255,0.3)'}`,
                    borderRadius: '8px', width: '100%', minHeight: playerEnlarged ? '48px' : '40px', cursor: 'pointer',
                    color: showAlbumTracks ? '#e0c0ff' : '#cc88ff', fontSize: playerEnlarged ? '14px' : '12px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.08em',
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0 12px', gap: '8px',
                  }}>☰  ALBUM TRACKS</button>
                )}
                {showAlbumTracks && currentMusicTrack.album_name && (
                  <div style={{
                    maxHeight: playerEnlarged ? '260px' : '160px', overflowY: 'auto',
                    background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(155,48,255,0.15)', borderRadius: '8px',
                    padding: '6px',
                  }}>
                    {albumTracksLoading ? (
                      <div style={{ color: '#5a5a7a', fontSize: '11px', fontFamily: 'monospace', padding: '8px' }}>[ LOADING TRACKLIST... ]</div>
                    ) : !albumTrackNames || albumTrackNames.length === 0 ? (
                      <div style={{ color: '#5a5a7a', fontSize: '11px', fontFamily: 'monospace', padding: '8px' }}>[ NO TRACKLIST AVAILABLE ]</div>
                    ) : (
                      albumTrackNames.map((name, i) => {
                        const isCurrent = name.toLowerCase() === (currentMusicTrack.title || '').toLowerCase();
                        const isResolving = resolvingTrackName === name;
                        return (
                          <div key={i} onClick={() => !isCurrent && playAlbumTrackByName(name)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 8px', borderRadius: '5px',
                              cursor: isCurrent ? 'default' : 'pointer',
                              background: isCurrent ? 'rgba(155,48,255,0.2)' : 'transparent',
                              transition: 'background 120ms',
                            }}
                            onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = 'rgba(155,48,255,0.1)'; }}
                            onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}>
                            <span style={{ fontSize: '10px', color: isCurrent ? '#e0c0ff' : '#5a5a7a', minWidth: '14px', fontFamily: 'monospace' }}>{i + 1}</span>
                            <span style={{
                              flex: 1, fontSize: '12px', fontFamily: 'Rajdhani, sans-serif',
                              color: isCurrent ? '#fff' : 'rgba(224,192,255,0.75)', fontWeight: isCurrent ? '700' : '500',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{isCurrent ? '▶ ' : ''}{name}</span>
                            {isResolving && <span style={{ fontSize: '9px', color: '#9b30ff', fontFamily: 'monospace', flexShrink: 0 }}>...</span>}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
                {/* 2026-08-29 owner request: search bar right in the player so any song
                    can be played without leaving it -- distinct from ALBUM TRACKS above,
                    which is scoped to the current track's own album. */}
                <button onClick={() => setPlayerSearchOpen(v => !v)} style={{
                  background: playerSearchOpen ? 'rgba(155,48,255,0.3)' : 'rgba(155,48,255,0.12)',
                  border: `1px solid ${playerSearchOpen ? '#9b30ff' : 'rgba(155,48,255,0.3)'}`,
                  borderRadius: '8px', width: '100%', minHeight: playerEnlarged ? '48px' : '40px', cursor: 'pointer',
                  color: playerSearchOpen ? '#e0c0ff' : '#cc88ff', fontSize: playerEnlarged ? '14px' : '12px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.08em',
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '0 12px', gap: '8px',
                }}>🔍  SEARCH</button>
                {playerSearchOpen && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <input
                      autoFocus
                      value={playerSearchQuery}
                      onChange={e => playerRunSearch(e.target.value)}
                      placeholder="SEARCH FOR A TRACK..."
                      style={{
                        width: '100%', boxSizing: 'border-box', background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(155,48,255,0.3)', borderRadius: '6px', padding: '8px 10px',
                        color: '#e0c0ff', fontSize: '12px', fontFamily: 'Rajdhani, sans-serif', outline: 'none',
                      }}
                    />
                    {(playerSearching || playerSearchResults) && (
                      <div style={{
                        maxHeight: playerEnlarged ? '260px' : '160px', overflowY: 'auto',
                        background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(155,48,255,0.15)', borderRadius: '8px',
                        padding: '6px',
                      }}>
                        {playerSearching ? (
                          <div style={{ color: '#5a5a7a', fontSize: '11px', fontFamily: 'monospace', padding: '8px' }}>[ SEARCHING... ]</div>
                        ) : playerSearchResults.length === 0 ? (
                          <div style={{ color: '#5a5a7a', fontSize: '11px', fontFamily: 'monospace', padding: '8px' }}>[ NO RESULTS ]</div>
                        ) : (
                          playerSearchResults.map((t, i) => (
                            <div key={i} onClick={() => playerPlaySearchResult(t)}
                              style={{
                                display: 'flex', flexDirection: 'column', padding: '7px 8px', borderRadius: '5px',
                                cursor: 'pointer', transition: 'background 120ms',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(155,48,255,0.1)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                              <span style={{ fontSize: '12px', fontFamily: 'Rajdhani, sans-serif', color: 'rgba(224,192,255,0.9)', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                              <span style={{ fontSize: '10px', color: '#5a5a7a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.artist}</span>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
                {playerPos && (
                  <button onClick={() => setPlayerPos(null)} style={{
                    background: 'transparent', border: '1px solid rgba(155,48,255,0.2)',
                    borderRadius: '8px', width: '100%', minHeight: '30px', cursor: 'pointer',
                    color: '#5a5a7a', fontSize: '10px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.06em',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>⟲  RE-DOCK TO EDGE</button>
                )}
                <button onClick={musicStop} style={{
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px', width: '100%', minHeight: '34px', cursor: 'pointer',
                  color: '#3a3a5a', fontSize: '11px', fontFamily: 'Orbitron, monospace', letterSpacing: '0.08em',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>✕  CLOSE</button>
              </>
            )}
          </div>
          </>
        );
      })()}
      {showJester && <JesterPopup onDone={() => setShowJester(false)} />}

      {/* ── ADULT ready-to-watch popup + dedicated player, app-level so both work
          regardless of what screen the user is currently on ── */}
      <Suspense fallback={null /* both are small popups over existing content -- no visible
          loading state needed for the brief gap while their chunk fetches */}>
        {adultReadyItem && !adultReadyPlayer && (
          <ReadyToWatchModal
            item={adultReadyItem}
            onClose={() => setAdultReadyItem(null)}
            onPlayNow={() => {
              markAdultWatched(adultReadyItem.infoHash, adultReadyItem.title);
              setAdultReadyPlayer({ url: buildPlayableUrl(adultReadyItem.streamUrl), title: adultReadyItem.title });
              setAdultReadyItem(null);
            }}
          />
        )}
        {adultReadyPlayer && (
          <PlayerOverlay
            streamUrl={adultReadyPlayer.url}
            title={adultReadyPlayer.title}
            onClose={() => setAdultReadyPlayer(null)}
          />
        )}
      </Suspense>
      {session ? (
        <>
          <SideNav
              currentScreenType={screen.type}
              onNavigate={navigateGuarded}
              isAdmin={Boolean(session.is_admin)}
              showAdult={Boolean(session.adult_content_enabled)}
              onLogout={() => { apiLogout(); setSession(null); clearSessionToken(); replaceScreen({ type: 'login' }); }}
            />
          {parentalGate && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,4,12,0.9)', backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
              <div style={{ width: '100%', maxWidth: '380px', background: '#0d0a1a', border: '1px solid rgba(176,38,255,0.3)',
                borderRadius: '10px', padding: '28px', boxShadow: '0 0 40px rgba(176,38,255,0.15)' }}>
                <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '10px', color: '#b026ff',
                  letterSpacing: '0.2em', marginBottom: '20px' }}>PARENTAL PASSWORD REQUIRED</div>
                <input type="password" autoFocus value={parentalGatePw}
                  onChange={e => setParentalGatePw(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitParentalGate(); }}
                  placeholder="Parental password"
                  style={{ width: '100%', background: 'rgba(176,38,255,0.06)', border: '1px solid rgba(176,38,255,0.2)',
                    borderRadius: '4px', padding: '10px 14px', color: '#e8e0f0', fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '15px', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }} />
                {parentalGateErr && <div style={{ color: '#ff2a55', fontFamily: 'Rajdhani, sans-serif', fontSize: '13px', marginBottom: '12px' }}>⚠ {parentalGateErr}</div>}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={() => { setParentalGate(null); setParentalGatePw(''); }} disabled={parentalGateBusy}
                    style={{ flex: 1, padding: '12px', background: 'transparent', border: '1px solid rgba(90,90,110,0.4)',
                      borderRadius: '4px', color: 'rgba(232,224,240,0.6)', fontFamily: 'Orbitron, monospace', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                    CANCEL
                  </button>
                  <button onClick={submitParentalGate} disabled={parentalGateBusy}
                    style={{ flex: 1, padding: '12px', background: parentalGateBusy ? 'rgba(176,38,255,0.4)' : '#b026ff',
                      border: 'none', borderRadius: '4px', color: '#07070b', fontFamily: 'Orbitron, monospace',
                      fontSize: '12px', fontWeight: '700', cursor: parentalGateBusy ? 'default' : 'pointer', letterSpacing: '0.08em' }}>
                    {parentalGateBusy ? 'CHECKING...' : 'UNLOCK'}
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="app-content">
          <Suspense fallback={<ScreenLoadingFallback />}>
          {screen.type === 'search' ? (
            <div style={pageStyle}>
              <SearchPage
                onMovieClick={(tmdbId) => pushScreen({ type: 'movie', tmdbId })}
                onTVClick={(tvId) => pushScreen({ type: 'tv-detail', tvId })}
                onActorClick={(personId) => pushScreen({ type: 'actor', personId })}
              />
            </div>
          ) : screen.type === 'movie' ? (
            <div style={pageStyle}>
              <MovieDetailPage
                tmdbId={screen.tmdbId}
                onMovieClick={(id) => pushScreen({ type: "movie", tmdbId: id })}
                onBack={popScreen}
                onActorClick={(personId) => pushScreen({ type: 'actor', personId })}
            isAdmin={Boolean(session.is_admin)}
                firstName={session?.first_name}
                movieId={screen.movieId}
                onMovieDeleted={() => pushScreen({ type: 'library-browse' })}
                autoResume={screen.autoResume}
              />
            </div>
          ) : screen.type === 'actor' ? (
            <div style={pageStyle}>
              <ActorDetailPage
                personId={screen.personId}
                onBack={popScreen}
                onMovieClick={(tmdbId) => pushScreen({ type: 'movie', tmdbId })}
                onTVClick={(tvId) => pushScreen({ type: 'tv-detail', tvId })}
              />
            </div>
          ) : screen.type === 'movies-browse' ? (
            <div style={pageStyle}>
              <MoviesPage
                onBack={popScreen}
                onOpenMovie={(tmdbId) => pushScreen({ type: 'movie', tmdbId })}
                onOpenLibrary={() => pushScreen({ type: 'library-browse' })}
                onOpenUserVault={() => pushScreen({ type: 'user-vault' })}
                onActorClick={(personId) => pushScreen({ type: 'actor', personId })}
            isAdmin={Boolean(session.is_admin)}
                firstName={session.first_name}
              />
            </div>
          ) : screen.type === 'library-browse' ? (
            <div style={pageStyle}>
              <LibraryPage
                onBack={popScreen}
                onOpenMovie={(tmdbId, movieId) => pushScreen({ type: 'movie', tmdbId, movieId })}
                isAdmin={Boolean(session.is_admin)}
              />
            </div>
          ) : screen.type === 'active-users' ? (
            <div style={pageStyle}>
              <ActiveUsersPage onBack={popScreen} />
            </div>
          ) : screen.type === 'admin-stats' ? (
            <div style={pageStyle}>
              <AdminStatsPage onBack={popScreen} />
            </div>
      ) : screen.type === 'admin-panel' ? (
            <div style={pageStyle}>
              <AdminPanel onBack={popScreen} onNavigate={(s) => pushScreen({ type: s })} />
            </div>
          ) : screen.type === 'bad-movies' ? (
            <div style={pageStyle}>
              <AdminBadMoviesPage onBack={popScreen} />
            </div>
          ) : screen.type === 'admin-downloads' ? (
            <div style={pageStyle}>
              <AdminDownloadsPage onBack={popScreen} />
            </div>
          ) : screen.type === 'magnet-queue' ? (
            <div style={pageStyle}>
              <AdminMagnetQueuePage onBack={popScreen} />
            </div>
          ) : screen.type === 'account' ? (
            <div style={pageStyle}>
              <AccountPage
                session={session}
                onBack={popScreen}
                onSessionUpdate={(data) => setSession(s => ({ ...s, ...data }))}
                onGoToSecurity={() => pushScreen({ type: 'security' })}
              />
            </div>
          ) : screen.type === 'transfers' ? (
            <div style={pageStyle}>
              <TransfersPage session={session} onBack={popScreen} />
            </div>
          ) : screen.type === 'messages' ? (
            <div style={pageStyle}>
              <MessagesPage session={session} onBack={popScreen} />
            </div>
          ) : screen.type === 'user-vault' ? (
            <div style={pageStyle}>
              <UserVaultPage
                onTvClick={(tvId) => pushScreen({ type: 'tv-detail', tvId })}
                firstName={session.first_name}
                onBack={popScreen}
                onMovieClick={(id) => pushScreen({ type: 'movie', tmdbId: id })}
                onPlayTracks={musicPlayTrack}
                onShuffleTracks={musicShuffle}
                currentTrackId={currentMusicTrack?.id}
                isPlaying={musicPlaying}
                isAdmin={Boolean(session.is_admin)}
              />
            </div>
          ) : screen.type === 'tv-vault' ? (
        <div style={pageStyle}>
          <TVVaultPage
            onBack={popScreen}
            onOpenShow={(tvId) => pushScreen({ type: 'tv-detail', tvId })}
          />
        </div>
      ) : screen.type === 'tv-user-vault' ? (
        <div style={pageStyle}>
          <TVUserVaultPage
            firstName={session?.first_name}
            onBack={popScreen}
            onTvClick={(tvId, resume) => pushScreen({ type: 'tv-detail', tvId, resumeSeason: resume?.resumeSeason, resumeEpisode: resume?.resumeEpisode, autoResume: resume?.autoResume })}
          />
        </div>
      ) : screen.type === 'tv' ? (
        <div style={pageStyle}>
          <TVPage
            onBack={popScreen}
            onOpenShow={(tvId) => pushScreen({ type: 'tv-detail', tvId })}
            onActorClick={(personId) => pushScreen({ type: 'actor', personId })}
            isAdmin={Boolean(session.is_admin)}
            onOpenVault={() => pushScreen({ type: 'tv-vault' })}
            onOpenUserVault={() => pushScreen({ type: 'tv-user-vault' })}
            firstName={session?.first_name}
          />
        </div>
      ) : screen.type === 'tv-detail' ? (
        <div style={pageStyle}>
          <TVDetailPage
            tvId={screen.tvId}
            onBack={popScreen}
            onOpenShow={(tvId) => pushScreen({ type: 'tv-detail', tvId })}
            onActorClick={(personId) => pushScreen({ type: 'actor', personId })}
            isAdmin={Boolean(session.is_admin)}
            firstName={session?.first_name}
            resumeSeason={screen.resumeSeason}
            resumeEpisode={screen.resumeEpisode}
            autoResume={screen.autoResume}
          />
        </div>
      ) : screen.type === 'anime' ? (
        // 2026-08-16: dedicated Anime page, mirrors the 'tv' branch above.
        <div style={pageStyle}>
          <AnimePage
            onBack={popScreen}
            onOpenShow={(tvId) => pushScreen({ type: 'anime-detail', tvId })}
            onActorClick={(personId) => pushScreen({ type: 'actor', personId })}
          />
        </div>
      ) : screen.type === 'comics' ? (
        // 2026-08-26: dedicated Comic Books page, backed by Jackett (see ComicsPage.jsx /
        // handlers/comics.go).
        <div style={pageStyle}>
          <ComicsPage onBack={popScreen} />
        </div>
      ) : screen.type === 'anime-detail' ? (
        // Reuses TVDetailPage as-is (episode list/season tabs/everything) -- mode="anime"
        // just routes the STREAM button through /anime/instant-play instead of
        // /tv/instant-play. See TVDetailPage.jsx and api.js's instantPlayTv.
        <div style={pageStyle}>
          <TVDetailPage
            tvId={screen.tvId}
            mode="anime"
            onBack={popScreen}
            onOpenShow={(tvId) => pushScreen({ type: 'anime-detail', tvId })}
            onActorClick={(personId) => pushScreen({ type: 'actor', personId })}
            isAdmin={Boolean(session.is_admin)}
            firstName={session?.first_name}
          />
        </div>
      ) : screen.type && screen.type.startsWith('network-') ? (
            <div style={pageStyle}><NetworkPage
              networkId={screen.type.replace('network-', '')}
              onMovieClick={(tmdbId) => pushScreen({ type: 'movie', tmdbId })}
              onTVClick={(tmdbId) => pushScreen({ type: 'tv-detail', tvId: tmdbId })}
            /></div>
          ) : screen.type === 'software' ? (
          <div style={pageStyle}><SoftwarePage /></div>
        ) : screen.type === 'games' ? (
            <div style={pageStyle}>
              <WiiGamesPage onBack={popScreen} />
            </div>
          ) : screen.type === 'music' ? (
            <div style={pageStyle}><MusicPage onGoHome={() => pushScreen({ type: 'dashboard' })} onOpenVault={() => pushScreen({ type: 'user-vault' })} onPlayTracks={musicPlayTrack} onAppendQueue={musicAppendQueue} onShuffleTracks={musicShuffle} currentTrackId={currentMusicTrack?.id} isPlaying={musicPlaying} isAdmin={Boolean(session.is_admin)} sessionToken={session?.session_token} /></div>
          ) : screen.type === 'adult' ? (
            // Defense in depth alongside SideNav hiding the nav item -- direct navigation
            // (e.g. a stale link from before the account opted out) still can't land here.
            Boolean(session.adult_content_enabled)
              ? <div style={pageStyle}><AdultPage onBack={popScreen} registerAdultJob={registerAdultJob} /></div>
              : <div style={{ minHeight: '100vh' }} />
          ) : screen.type === 'contact' ? (
            <div style={pageStyle}><ContactPage /></div>
          ) : screen.type === 'bbs' ? (
            <BBSPage sessionToken={session?.session_token} username={session?.username} playerActive={!!currentMusicTrack} />
          ) : screen.type === 'forum' ? (
        <div style={pageStyle}>
          <ForumPage onBack={popScreen} />
        </div>
      ) : screen.type === 'security' ? (
        <SecurityPage
          onBack={popScreen}
          onGoToTotpSetup={() => pushScreen({ type: 'totp-setup' })}
        />
      ) : screen.type === 'totp-setup' ? (
            <div style={pageStyle}>
              <TotpSetupPage onBack={popScreen} />
            </div>
          ) : (
            <div style={{ minHeight: '100vh' }}>
              <Dashboard
                onSelect={(key) => navigateGuarded(key === 'movies' ? 'movies-browse' : key)}
                onNavigate={(key, data) => {
                  // 2026-08-17 (Watch Later resume-in-place): carry the resume point through
                  // so MovieDetailPage/TVDetailPage can auto-launch playback at that exact
                  // spot instead of just opening the detail page.
                  if (key === 'movie-detail' && data?.tmdbId) return pushScreen({ type: 'movie', tmdbId: data.tmdbId, autoResume: data.autoResume });
                  if (key === 'tv-detail' && data?.tmdbId) return pushScreen({ type: 'tv-detail', tvId: data.tmdbId, resumeSeason: data.resumeSeason, resumeEpisode: data.resumeEpisode, autoResume: data.autoResume });
                  navigateGuarded(key === 'movies' ? 'movies-browse' : key);
                }}
                isAdmin={Boolean(session.is_admin)}
                session={session}
                onOpenMovie={(tmdbId) => pushScreen({ type: 'movie', tmdbId })}
                onLogout={() => { apiLogout(); setSession(null); clearSessionToken(); replaceScreen({ type: 'login' }); }}
              />
            </div>
          )}
          </Suspense>
          </div>
        </>
      ) : screen.type === 'signup' ? (
        <div style={pageStyle}>
          <SignupForm
            onBack={() => popScreen()}
            onSignedUp={(result, email) => {
              if (result && result.session_token) {
                // Open registration: logged in immediately, verification is a skippable gate.
                setSessionToken(result.session_token);
                setSession(result);
                setVerifyEmailAddr(email);
                replaceScreen({ type: 'verify-email' });
                // Inside the app's signup WebView: hand the session to the native app
                // so it's logged in the moment the user finishes here.
                if (window.AetherisApp && window.AetherisApp.sessionCreated) {
                  window.AetherisApp.sessionCreated(result.session_token);
                }
              } else {
                replaceScreen({ type: 'pending' });
              }
            }}
          />
        </div>
      ) : screen.type === 'verify-email' ? (
        <div style={pageStyle}>
          <VerifyEmailPage
            email={verifyEmailAddr}
            onVerified={() => replaceScreen({ type: 'dashboard' })}
            onSkip={() => replaceScreen({ type: 'dashboard' })}
          />
        </div>
      ) : screen.type === 'legal-docs' ? (
        <div style={pageStyle}>
          <div style={{ color: 'white', padding: '40px' }}>
            <p>Redirecting...</p>
          </div>
        </div>
      ) : screen.type === 'pending' ? (
        <div style={pageStyle}>
          <div style={{ color: 'white', padding: '40px', maxWidth: '480px' }}>
            <h1>Account Pending Approval</h1>
            <p>Your account request has been received.</p>
            <p>An administrator must review and approve your account before you can log in.</p>
            <button
              type="button"
              onClick={() => replaceScreen({ type: 'login' })}
              style={{ padding: '10px 20px', marginTop: '12px' }}
            >
              Back to Login
            </button>
          </div>
        </div>
      ) : screen.type === 'admin-login' ? (
        <div style={pageStyle}>
          <AdminLoginForm
            onLoggedIn={(result) => {
              if (result.totp_required) {
                setTotpPendingUserId(result.user_id);
                pushScreen({ type: 'totp-login' });
              } else {
                localStorage.removeItem('aetheris_recently_watched');
                setSession(result);
                pushScreen({ type: 'dashboard' });
              }
            }}
            onBack={popScreen}
          />
        </div>
      ) : screen.type === 'totp-login' ? (
        <div style={pageStyle}>
          <TotpLoginStep
            userId={totpPendingUserId}
            onSuccess={(result) => {
              localStorage.removeItem('aetheris_recently_watched');
              setSessionToken(result.session_token);
              setSession(result);
              replaceScreen({ type: 'dashboard' });
            }}
            onBack={() => replaceScreen({ type: 'login' })}
          />
        </div>
      ) : screen.type && screen.type.startsWith('network-') ? (
            <div style={pageStyle}><NetworkPage
              networkId={screen.type.replace('network-', '')}
              onMovieClick={(tmdbId) => pushScreen({ type: 'movie', tmdbId })}
              onTVClick={(tmdbId) => pushScreen({ type: 'tv-detail', tvId: tmdbId })}
            /></div>
          ) : screen.type === 'music' ? (
            <div style={pageStyle}><MusicPage onGoHome={() => pushScreen({ type: 'dashboard' })} onOpenVault={() => pushScreen({ type: 'user-vault' })} onPlayTracks={musicPlayTrack} onAppendQueue={musicAppendQueue} onShuffleTracks={musicShuffle} currentTrackId={currentMusicTrack?.id} isPlaying={musicPlaying} isAdmin={Boolean(session.is_admin)} sessionToken={session?.session_token} /></div>
          ) : screen.type === 'contact' ? (
            <div style={pageStyle}><ContactPage /></div>
          ) : screen.type === 'bbs' ? (
            <BBSPage sessionToken={session?.session_token} username={session?.username} playerActive={!!currentMusicTrack} />
          ) : screen.type === 'forum' ? (
        <div style={pageStyle}>
          <ForumPage onBack={popScreen} />
        </div>
      ) : screen.type === 'security' ? (
        <SecurityPage
          onBack={popScreen}
          onGoToTotpSetup={() => pushScreen({ type: 'totp-setup' })}
        />
      ) : screen.type === 'totp-setup' ? (
        <div style={pageStyle}>
          <TotpSetupPage onBack={popScreen} />
        </div>
      ) : screen.type === 'forgot-password' ? (
        <div style={pageStyle}>
          <ForgotPasswordFlow
            onBack={() => replaceScreen({ type: 'login' })}
            onResetSent={() => replaceScreen({ type: 'login' })}
          />
        </div>
      ) : screen.type === 'reset-password' ? (
        <div style={pageStyle}>
          <ResetPasswordPage
            onDone={() => {
              window.history.replaceState({}, '', '/');
              replaceScreen({ type: 'login' });
            }}
          />
        </div>
      ) : (
        <div style={pageStyle}>
          <LoginForm
            onLoggedIn={(result) => {
              if (result.totp_required) {
                setTotpPendingUserId(result.user_id);
                pushScreen({ type: 'totp-login' });
              } else {
                localStorage.removeItem('aetheris_recently_watched');
                setSession(result);
                if (result.email_verified === false) {
                  setVerifyEmailAddr('');
                  pushScreen({ type: 'verify-email' });
                } else {
                  pushScreen({ type: 'dashboard' });
                }
              }
            }}
            onGoToSignup={() => pushScreen({ type: 'signup' })}
            onPendingApproval={() => replaceScreen({ type: 'pending' })}
            onGoToAdminLogin={() => pushScreen({ type: 'admin-login' })}
            onForgotPassword={() => pushScreen({ type: 'forgot-password' })}
          />
        </div>
      )}
    </>
  );
}

// 2026-08-27: wraps the whole app so a stale-tab lazy-chunk failure (see
// ChunkErrorBoundary's own comment) recovers with an automatic reload instead of a
// silent black screen -- this was the actual cause of the reported "ADMIN shows a
// black page" bug.
function App() {
  return (
    <ChunkErrorBoundary>
      <AppInner />
    </ChunkErrorBoundary>
  );
}

export default App;
